import React, { useContext, useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { AuthContext } from "../../../../config/AuthContext";
import { Table, Tag, Card, Button, Typography, Space, Input, Row, Col, Modal, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import { BiRefresh, BiBookOpen, BiPlayCircle } from "react-icons/bi";
import { BookOutlined, CheckCircleOutlined, SearchOutlined, PlaySquareOutlined } from '@ant-design/icons';
import AppSider from "../../components/AppSider";
import { Content } from "antd/es/layout/layout";
import { GetConjuntsPildores } from "../../../../dao/jutge/GetConjuntsPildores";

const { Title, Text, Paragraph } = Typography;

export const Pildores: React.FC = () => {
    const { isLoggedIn, userId, setBreadCrumb } = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const [pildores, setPildores] = useState<any[]>([]);
    const [searchText, setSearchText] = useState('');
    const [selectedVideo, setSelectedVideo] = useState<any>(null);

    useEffect(() => {
        if (isLoggedIn) {
            setBreadCrumb([
                { nom: 'Home', nav: '/home' },
                { nom: 'database', nav: '/database' },
                { nom: 'pildores', nav: '/database/pildores' }
            ], true);
            checkDades();
        }
    }, [isLoggedIn]);

    const checkDades = async () => {
        setLoading(true);
        try {
            const data = await GetConjuntsPildores(userId + "");
            setPildores(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error("Error al carregar píldores:", e);
        } finally {
            setLoading(false);
        }
    };

    const filteredPildores = pildores.filter(p =>
        (p.nom || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (p.descripcio || '').toLowerCase().includes(searchText.toLowerCase())
    );

    const columns: ColumnsType<any> = [
        {
            title: "Píldora Teòrica",
            dataIndex: "nom",
            key: "nom",
            render: (text, record) => (
                <Space>
                    <BookOutlined style={{ color: '#52c41a', fontSize: 18 }} />
                    <div>
                        <Text strong style={{ fontSize: 14 }}>{text}</Text>
                        <div style={{ fontSize: 12, color: '#888' }}>
                            {record.descripcio || "Càpsula d'aprenentatge sobre conceptes clau de bases de dades."}
                        </div>
                    </div>
                </Space>
            )
        },
        {
            title: "Categoria / Tipus",
            dataIndex: "tipus",
            key: "tipus",
            width: 140,
            render: (val) => <Tag color="cyan">{val || "SQL / Disseny"}</Tag>
        },
        {
            title: "Acció",
            key: "action",
            width: 120,
            align: "center",
            render: (_, record) => (
                <Button
                    type="primary"
                    icon={<PlaySquareOutlined />}
                    onClick={() => setSelectedVideo(record)}
                    style={{ background: '#1890ff', borderColor: '#1890ff' }}
                >
                    Veure
                </Button>
            )
        }
    ];

    return (
        <div style={{ display: 'flex', width: '100%', minHeight: '100vh', background: '#f8f9fb' }}>
            <AppSider currentVal={6} />
            <Content style={{ padding: '24px', flex: 1 }}>
                <Card
                    bordered={false}
                    style={{
                        borderRadius: 16,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                        background: '#fff'
                    }}
                >
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 20,
                        flexWrap: 'wrap',
                        gap: 12
                    }}>
                        <div>
                            <Title level={3} style={{ margin: 0, color: '#1f2937' }}>
                                💊 Píldores de Base de Dades
                            </Title>
                            <Text type="secondary">
                                Vídeos curts, conceptes i càpsules d'aprenentatge ràpid sobre SQL, DDL i disseny.
                            </Text>
                        </div>

                        <Space>
                            <Input
                                placeholder="Cercar píldores..."
                                prefix={<SearchOutlined />}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                style={{ width: 240, borderRadius: 8 }}
                            />
                            <Button
                                icon={<BiRefresh />}
                                onClick={checkDades}
                                loading={loading}
                            >
                                Refrescar
                            </Button>
                        </Space>
                    </div>

                    <Table
                        columns={columns}
                        dataSource={filteredPildores}
                        rowKey={(r, idx) => r.id || idx}
                        loading={loading}
                        pagination={{ pageSize: 10 }}
                        bordered={false}
                    />
                </Card>

                {/* Modal Visualitzador de Píldora */}
                <Modal
                    title={selectedVideo?.nom || "Píldora Teòrica"}
                    open={!!selectedVideo}
                    onCancel={() => setSelectedVideo(null)}
                    footer={[
                        <Button key="close" onClick={() => setSelectedVideo(null)}>
                            Tancar
                        </Button>
                    ]}
                    width={720}
                >
                    <div style={{ padding: '12px 0' }}>
                        <Paragraph>
                            {selectedVideo?.descripcio || "Aquesta píldora sintetitza els conceptes fonamentals per dominar aquest bloc."}
                        </Paragraph>

                        {selectedVideo?.url ? (
                            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
                                <iframe
                                    src={selectedVideo.url}
                                    title="Pildora Video"
                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                                    allowFullScreen
                                />
                            </div>
                        ) : (
                            <div style={{
                                height: 260,
                                background: '#f5f7fa',
                                borderRadius: 12,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px dashed #d9d9d9'
                            }}>
                                <PlaySquareOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 12 }} />
                                <Text strong>Material Teòric i Explicatiu</Text>
                                <Text type="secondary" style={{ marginTop: 4 }}>
                                    Visualitza els apunts complets a la secció de Teoria o a l'IDE Online.
                                </Text>
                            </div>
                        )}
                    </div>
                </Modal>
            </Content>
        </div>
    );
};

export default Pildores;
